import React, { useEffect, useMemo, useState } from 'react';
import {
  FiAlertCircle,
  FiArrowRight,
  FiChevronDown,
  FiDollarSign,
  FiInbox,
  FiMessageCircle,
  FiRefreshCw,
  FiServer,
  FiTrendingDown,
} from 'react-icons/fi';
import { Link } from 'react-router-dom';

import {
  Badge,
  Button,
  Card,
  EmptyState,
  IconButton,
  PageHeader,
  Skeleton,
  StatTile,
  cn,
} from '../components/ui';
import { formatUsd } from '../servicesV2/aiSpendService';
import { getOverview } from '../servicesV2/entrepriseService';
import { getPendingRequestsCount } from '../servicesV2/partnerService';

/* -------------------------------------------------------------------------- */
/*  Config & helpers                                                          */
/* -------------------------------------------------------------------------- */

const FAMILY_LABELS = {
  technique: 'Technique',
  couts: 'Coûts',
  commercial: 'Commercial',
};

const SEVERITY_RANK = { danger: 0, warning: 1, info: 2 };

const bySeverity = (a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];

// Subjects that ask for a decision (info ones are only context).
const isOpen = (subject) => subject.severity === 'danger' || subject.severity === 'warning';

const hasOpen = (tenant, family) =>
  tenant.subjects.some((s) => isOpen(s) && (!family || s.family === family));

const countOpen = (tenant, severity) => tenant.subjects.filter((s) => s.severity === severity).length;

// Worst open severity across all tenants for one subject kind (tile tone).
const worstSeverity = (tenants, kind) => {
  const open = tenants.flatMap((t) => t.subjects).filter((s) => isOpen(s) && s.kind === kind);
  if (open.some((s) => s.severity === 'danger')) return 'danger';
  return open.length > 0 ? 'warning' : 'neutral';
};

// Worst open severity among a tenant's subjects of the given kinds, or null.
const kindSeverity = (tenant, kinds) => {
  const open = tenant.subjects.filter((s) => isOpen(s) && kinds.includes(s.kind));
  if (open.some((s) => s.severity === 'danger')) return 'danger';
  return open.length > 0 ? 'warning' : null;
};

const formatTime = (iso) =>
  new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

const formatDayMonth = (iso) =>
  new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });

const formatMonthYear = (iso) =>
  new Date(iso).toLocaleDateString('fr-FR', { month: '2-digit', year: 'numeric', timeZone: 'UTC' });

const plural = (n, one, many) => (n === 1 ? one : many);

const DAY_MS = 24 * 60 * 60 * 1000;

// From this many clients on, pending base updates are a platform matter (a sweep to run),
// shown once above the list instead of repeated on every row.
const MIGRATIONS_ROLLUP_MIN = 3;

// Contract bars are drawn on ±24 months; longer contracts are capped (the label keeps the date).
const CONTRACT_SCALE_MONTHS = 24;

// Bar fill: the colour carries meaning only when the row has a subject on that measure.
const BAR_TONES = {
  danger: 'bg-danger-500',
  warning: 'bg-warning-500',
};

const LABEL_TONES = {
  danger: 'text-danger-600 dark:text-danger-500',
  warning: 'text-warning-600 dark:text-warning-500',
};

const percentOf = (value, max) => (max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0);

/* -------------------------------------------------------------------------- */
/*  Bars                                                                      */
/* -------------------------------------------------------------------------- */

function BarCell({ label, labelTone, children }) {
  return (
    <div className="min-w-0">
      <div className="relative h-3 w-full rounded-sm bg-surface-muted">{children}</div>
      <p className={cn('mt-1 truncate text-caption tabular-nums', LABEL_TONES[labelTone] || 'text-fg-muted')}>
        {label}
      </p>
    </div>
  );
}

/** Months left on the contract: right of the centre line = remaining, left = expired. */
function ContractBar({ tenant, now }) {
  if (!tenant.endDate) return <BarCell label="sans date de fin" />;
  const end = new Date(tenant.endDate);
  const months = (end.getTime() - now) / (30.44 * DAY_MS);
  const capped = Math.max(-CONTRACT_SCALE_MONTHS, Math.min(CONTRACT_SCALE_MONTHS, months));
  const width = (Math.abs(capped) / CONTRACT_SCALE_MONTHS) * 50;
  const tone = kindSeverity(tenant, ['contract']);
  const label = months < 0 ? `échu ${formatMonthYear(tenant.endDate)}` : `jusqu'au ${formatMonthYear(tenant.endDate)}`;
  return (
    <BarCell label={label} labelTone={tone}>
      <span className="absolute inset-y-[-3px] left-1/2 w-px bg-line-strong" aria-hidden="true" />
      <span
        className={cn('absolute inset-y-0 rounded-sm', BAR_TONES[tone] || 'bg-fg-subtle')}
        style={months < 0 ? { right: '50%', width: `max(${width}%, 2px)` } : { left: '50%', width: `max(${width}%, 2px)` }}
      />
    </BarCell>
  );
}

/** Writes over the last 30 days, on the scale of the busiest client. */
function UsageBar({ tenant, max, now }) {
  const { activity } = tenant;
  if (!activity) return <BarCell label="inconnu" />;
  const tone = kindSeverity(tenant, ['activity']) || (activity.events30 === 0 ? kindSeverity(tenant, ['contract']) : null);
  let label = `${activity.events30} saisie${activity.events30 > 1 ? 's' : ''}`;
  if (activity.events30 > 0 && activity.lastAt) label += ` · dernière ${formatDayMonth(activity.lastAt)}`;
  if (activity.events30 === 0) {
    label = activity.lastAt
      ? `aucune depuis ${Math.floor((now - new Date(activity.lastAt).getTime()) / DAY_MS)} j`
      : 'aucune saisie enregistrée';
  }
  return (
    <BarCell label={label} labelTone={tone}>
      {activity.events30 > 0 && (
        <span
          className={cn('absolute inset-y-0 left-0 rounded-sm', BAR_TONES[tone] || 'bg-brand-500')}
          style={{ width: `max(${percentOf(activity.events30, max)}%, 2px)` }}
        />
      )}
    </BarCell>
  );
}

/** AI spend of the month: solid = spent, hatched = end-of-month estimate, tick = threshold. */
function AiBar({ tenant, max }) {
  const { ai } = tenant;
  if (!ai || (ai.spentUsd <= 0 && !(ai.budgetUsd > 0))) return <BarCell label="—" />;
  const tone = kindSeverity(tenant, ['ai']);
  const spent = percentOf(ai.spentUsd, max);
  const projected = percentOf(Math.max(ai.projectedUsd, ai.spentUsd), max);
  const label =
    ai.budgetUsd != null ? `${formatUsd(ai.spentUsd)} sur ${formatUsd(ai.budgetUsd)}` : formatUsd(ai.spentUsd);
  return (
    <BarCell label={label} labelTone={tone}>
      <span
        className="absolute inset-y-0 left-0 rounded-sm"
        style={{
          width: `${projected}%`,
          backgroundImage: 'repeating-linear-gradient(135deg, rgb(var(--brand) / 0.35) 0 3px, transparent 3px 6px)',
        }}
      />
      {ai.spentUsd > 0 && (
        <span
          className={cn('absolute inset-y-0 left-0 rounded-sm', BAR_TONES[tone] || 'bg-brand-500')}
          style={{ width: `max(${spent}%, 2px)` }}
        />
      )}
      {ai.budgetUsd > 0 && (
        <span
          className="absolute inset-y-[-3px] w-0.5 bg-fg"
          style={{ left: `${percentOf(ai.budgetUsd, max)}%` }}
          title={`Seuil ${formatUsd(ai.budgetUsd)}`}
        />
      )}
    </BarCell>
  );
}

/** WhatsApp cost of the month: solid = spent, tick = budget. */
function WhatsAppBar({ tenant, max }) {
  const { whatsapp } = tenant;
  if (!whatsapp || (whatsapp.costUsd <= 0 && !(whatsapp.budgetUsd > 0))) return <BarCell label="—" />;
  const tone = kindSeverity(tenant, ['whatsapp']);
  const label =
    whatsapp.budgetUsd > 0
      ? `${formatUsd(whatsapp.costUsd)} sur ${formatUsd(whatsapp.budgetUsd)}`
      : formatUsd(whatsapp.costUsd);
  return (
    <BarCell label={label} labelTone={tone}>
      {whatsapp.costUsd > 0 && (
        <span
          className={cn('absolute inset-y-0 left-0 rounded-sm', BAR_TONES[tone] || 'bg-brand-500')}
          style={{ width: `max(${percentOf(whatsapp.costUsd, max)}%, 2px)` }}
        />
      )}
      {whatsapp.budgetUsd > 0 && (
        <span
          className="absolute inset-y-[-3px] w-0.5 bg-fg"
          style={{ left: `${percentOf(whatsapp.budgetUsd, max)}%` }}
          title={`Budget ${formatUsd(whatsapp.budgetUsd)}`}
        />
      )}
    </BarCell>
  );
}

/**
 * Enabled accounts as one 100 % bar read as "who really uses it": filled = logged in within
 * 30 days, light = 30 to 90 days, empty = dormant (no login for 90 days, or never).
 */
function UsersBar({ tenant }) {
  const { users } = tenant;
  if (!users) return <BarCell label="inconnu" />;
  if (users.total === 0) return <BarCell label="aucun compte" />;
  const share = (n) => `${(n / users.total) * 100}%`;
  return (
    <div className="min-w-0">
      <div className="relative flex h-3 w-full overflow-hidden rounded-sm bg-surface-muted ring-1 ring-inset ring-line">
        {users.active > 0 && <span className="bg-brand-500" style={{ width: share(users.active) }} />}
        {users.inactive > 0 && <span className="bg-brand-500/35" style={{ width: share(users.inactive) }} />}
      </div>
      <p className="mt-1 truncate text-caption tabular-nums text-fg">
        <span className="font-semibold">{users.active}</span> {plural(users.active, 'actif', 'actifs')} sur{' '}
        {users.total} {plural(users.total, 'compte', 'comptes')}
      </p>
      <p className="flex flex-wrap items-center gap-x-2 text-caption tabular-nums text-fg-muted">
        <span className="inline-flex items-center gap-1">
          <span className="h-2 w-2 rounded-full bg-brand-500/35" aria-hidden="true" />
          {users.inactive} {plural(users.inactive, 'inactif', 'inactifs')}
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="h-2 w-2 rounded-full ring-1 ring-inset ring-line-strong" aria-hidden="true" />
          {users.dormant} {plural(users.dormant, 'dormant', 'dormants')}
        </span>
        <span>
          {users.admins} {plural(users.admins, 'admin', 'admins')}
        </span>
      </p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Rows                                                                      */
/* -------------------------------------------------------------------------- */

const ROW_GRID =
  'grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,1.1fr)_repeat(5,minmax(0,1fr))] lg:gap-5';

// Column title repeated above each bar when the columns stack (no header row below lg).
function Cell({ title, children }) {
  return (
    <div className="min-w-0">
      <p className="mb-1 text-micro text-fg-subtle lg:hidden">{title}</p>
      {children}
    </div>
  );
}

function SubjectLine({ subject }) {
  const muted = subject.severity === 'info';
  return (
    <Link to={subject.link} className="group flex items-start gap-2 rounded-md py-0.5 hover:bg-surface-muted">
      <Badge variant={muted ? 'neutral' : subject.severity} size="sm" className="mt-0.5 shrink-0">
        {FAMILY_LABELS[subject.family]}
      </Badge>
      <span className={cn('min-w-0 text-body-sm group-hover:underline', muted ? 'text-fg-muted' : 'text-fg')}>
        {subject.text}
      </span>
    </Link>
  );
}

function OverviewRow({ tenant, scales, now, expanded, onToggle }) {
  const dangers = countOpen(tenant, 'danger');
  const warnings = countOpen(tenant, 'warning');
  const subjects = [...tenant.subjects].sort(bySeverity);
  return (
    <li>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className={cn(ROW_GRID, 'w-full px-4 py-3 text-left hover:bg-surface-muted')}
      >
        <div className="flex min-w-0 items-start gap-2 sm:col-span-2 lg:col-span-1">
          <FiChevronDown
            size={14}
            className={cn('mt-1 shrink-0 text-fg-subtle transition-transform', expanded ? 'rotate-0' : '-rotate-90')}
          />
          <div className="min-w-0">
            <p className="truncate text-body-sm font-semibold text-fg">{tenant.nom}</p>
            <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
              <span className="font-mono text-caption text-fg-subtle">{tenant.key}</span>
              {dangers > 0 && (
                <Badge variant="danger" size="sm">
                  {dangers} à traiter
                </Badge>
              )}
              {warnings > 0 && (
                <Badge variant="warning" size="sm">
                  {warnings} à surveiller
                </Badge>
              )}
            </div>
          </div>
        </div>
        <Cell title="Contrat">
          <ContractBar tenant={tenant} now={now} />
        </Cell>
        <Cell title="Utilisateurs">
          <UsersBar tenant={tenant} />
        </Cell>
        <Cell title="Usage 30 jours">
          <UsageBar tenant={tenant} max={scales.usage} now={now} />
        </Cell>
        <Cell title="IA ce mois">
          <AiBar tenant={tenant} max={scales.ai} />
        </Cell>
        <Cell title="WhatsApp ce mois">
          <WhatsAppBar tenant={tenant} max={scales.whatsapp} />
        </Cell>
      </button>
      {expanded && (
        <div className="flex flex-col gap-1 px-4 pb-3 md:pl-10">
          {subjects.length > 0 ? (
            subjects.map((s) => <SubjectLine key={`${s.kind}-${s.text}`} subject={s} />)
          ) : (
            <span className="text-body-sm text-fg-muted">Aucun sujet ouvert.</span>
          )}
          <Link to={`/edit-client/${tenant.id}`} className="mt-1 text-caption text-fg-muted hover:underline">
            Ouvrir la fiche client
          </Link>
        </div>
      )}
    </li>
  );
}

function ColumnHeaders() {
  return (
    <div className={cn(ROW_GRID, 'hidden border-b border-line px-4 py-2.5 lg:grid')}>
      <p className="text-micro text-fg-subtle">Client</p>
      <div>
        <p className="text-micro text-fg-subtle">Contrat</p>
        <p className="text-caption text-fg-subtle">gauche : échu · droite : restant (24 mois max)</p>
      </div>
      <div>
        <p className="text-micro text-fg-subtle">Utilisateurs</p>
        <p className="text-caption text-fg-subtle">plein : connecté ces 30 j · clair : 30 à 90 j · vide : dormant</p>
      </div>
      <div>
        <p className="text-micro text-fg-subtle">Usage 30 jours</p>
        <p className="text-caption text-fg-subtle">saisies, échelle du client le plus actif</p>
      </div>
      <div>
        <p className="text-micro text-fg-subtle">IA ce mois</p>
        <p className="text-caption text-fg-subtle">plein : dépensé · hachuré : fin de mois · trait : seuil</p>
      </div>
      <div>
        <p className="text-micro text-fg-subtle">WhatsApp ce mois</p>
        <p className="text-caption text-fg-subtle">plein : coût des messages · trait : budget</p>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Dashboard                                                                 */
/* -------------------------------------------------------------------------- */

function Dashboard() {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [pendingRequests, setPendingRequests] = useState(0);
  const [showQuiet, setShowQuiet] = useState(false);
  const [expandedKey, setExpandedKey] = useState(null);

  const fetchAll = async ({ isRefresh } = {}) => {
    if (isRefresh) setRefreshing(true);
    try {
      const [res, pending] = await Promise.allSettled([getOverview(), getPendingRequestsCount()]);
      if (res.status === 'fulfilled') setOverview(res.value);
      if (pending.status === 'fulfilled') setPendingRequests(pending.value);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const tenants = useMemo(() => overview?.tenants ?? [], [overview]);
  const migrationsTenants = useMemo(
    () => tenants.filter((t) => t.subjects.some((s) => s.kind === 'migrations')),
    [tenants],
  );
  const migrationsRolledUp = migrationsTenants.length >= MIGRATIONS_ROLLUP_MIN;
  // Rows: without the rolled-up migrations subject, most urgent first, then the most active.
  const rows = useMemo(() => {
    const list = migrationsRolledUp
      ? tenants.map((t) => ({ ...t, subjects: t.subjects.filter((s) => s.kind !== 'migrations') }))
      : tenants;
    return [...list].sort(
      (a, b) =>
        countOpen(b, 'danger') - countOpen(a, 'danger') ||
        countOpen(b, 'warning') - countOpen(a, 'warning') ||
        (b.activity?.events30 ?? -1) - (a.activity?.events30 ?? -1),
    );
  }, [tenants, migrationsRolledUp]);
  const withSubjects = useMemo(() => rows.filter((t) => hasOpen(t)), [rows]);
  const quiet = useMemo(() => rows.filter((t) => !hasOpen(t)), [rows]);
  // Shared scales so every row reads against the same axis.
  const scales = useMemo(
    () => ({
      usage: Math.max(0, ...tenants.map((t) => t.activity?.events30 ?? 0)),
      ai: Math.max(
        0,
        ...tenants.map((t) => (t.ai ? Math.max(t.ai.spentUsd, t.ai.projectedUsd, t.ai.budgetUsd ?? 0) : 0)),
      ),
      whatsapp: Math.max(
        0,
        ...tenants.map((t) => (t.whatsapp ? Math.max(t.whatsapp.costUsd, t.whatsapp.budgetUsd ?? 0) : 0)),
      ),
    }),
    [tenants],
  );

  if (loading) {
    return (
      <>
        <PageHeader title="Situation des clients" />
        <Skeleton className="h-9 w-80 rounded-md" />
        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-32 rounded-lg" />
          ))}
        </div>
        <Skeleton className="mt-5 h-80 rounded-lg" />
      </>
    );
  }

  if (!overview) {
    return (
      <>
        <PageHeader title="Situation des clients" />
        <EmptyState
          variant="danger"
          icon={<FiAlertCircle size={22} />}
          title="Chargement impossible"
          description="La situation des clients n'a pas pu être récupérée."
          action={<Button onClick={() => fetchAll({ isRefresh: true })}>Réessayer</Button>}
        />
      </>
    );
  }

  const now = new Date(overview.generatedAt).getTime();
  const { totals } = overview;
  const technicalCount = tenants.filter((t) => hasOpen(t, 'technique')).length;
  const commercialCount = rows.filter((t) => hasOpen(t, 'commercial')).length;
  const aiTone = worstSeverity(tenants, 'ai');
  const whatsappTone = worstSeverity(tenants, 'whatsapp');
  const whatsappUsers = tenants.filter((t) => t.whatsapp?.costUsd > 0).length;
  // Clients with a subject of their own; the rolled-up base updates are announced separately.
  const decisionCount = withSubjects.length;
  const renderRow = (t) => (
    <OverviewRow
      key={t.id}
      tenant={t}
      scales={scales}
      now={now}
      expanded={expandedKey === t.key}
      onToggle={() => setExpandedKey((k) => (k === t.key ? null : t.key))}
    />
  );

  return (
    <>
      <PageHeader
        title="Situation des clients"
        description={`${tenants.length} ${plural(tenants.length, 'client', 'clients')} · mis à jour à ${formatTime(overview.generatedAt)} · cliquer sur une ligne pour le détail`}
        actions={
          <IconButton
            variant="subtle"
            size="md"
            tooltip="Rafraîchir"
            onClick={() => fetchAll({ isRefresh: true })}
            disabled={refreshing}
          >
            <FiRefreshCw className={refreshing ? 'animate-spin' : ''} />
          </IconButton>
        }
      />

      <p className="text-h1 text-fg">
        {decisionCount === 0
          ? 'Aucun client ne demande de décision'
          : `${decisionCount} ${plural(decisionCount, 'client demande', 'clients demandent')} une décision`}
      </p>
      {migrationsRolledUp && (
        <p className="mt-1 text-body-sm text-fg-muted">
          et une mise à jour des bases à lancer pour {migrationsTenants.length} clients
        </p>
      )}

      <section className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile
          icon={<FiServer size={18} />}
          label="Ça ne marche pas"
          value={technicalCount}
          tone={technicalCount > 0 ? 'danger' : 'neutral'}
          hint={technicalCount > 0 ? plural(technicalCount, 'client concerné', 'clients concernés') : 'Rien à signaler'}
        />
        <StatTile
          icon={<FiDollarSign size={18} />}
          label="Coût IA ce mois"
          value={formatUsd(totals.aiSpentUsd)}
          tone={aiTone}
          hint={`≈ ${formatUsd(totals.aiProjectedUsd)} en fin de mois`}
        />
        <StatTile
          icon={<FiMessageCircle size={18} />}
          label="Coût WhatsApp ce mois"
          value={formatUsd(totals.whatsappCostUsd)}
          tone={whatsappTone}
          hint={
            whatsappUsers > 0
              ? `${whatsappUsers} ${plural(whatsappUsers, 'client utilise', 'clients utilisent')} WhatsApp`
              : 'Aucun message envoyé'
          }
        />
        <StatTile
          icon={<FiTrendingDown size={18} />}
          label="Risque commercial"
          value={commercialCount}
          tone={commercialCount > 0 ? 'warning' : 'neutral'}
          hint={commercialCount > 0 ? plural(commercialCount, 'client concerné', 'clients concernés') : 'Rien à signaler'}
        />
      </section>

      {pendingRequests > 0 && (
        <section className="mt-5">
          <div className="flex items-center justify-between rounded-lg border border-warning-100 bg-gradient-to-r from-warning-50 to-surface px-4 py-3 dark:border-warning-500/20 dark:from-warning-500/10">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-warning-100 text-warning-700 dark:bg-warning-500/15 dark:text-warning-500">
                <FiInbox size={18} />
              </span>
              <div>
                <p className="text-body-sm font-semibold text-fg">
                  {pendingRequests} demande{pendingRequests > 1 ? 's' : ''} de partenaire en attente
                </p>
                <p className="text-caption text-fg-muted">Nouvelles demandes de provisionnement à examiner.</p>
              </div>
            </div>
            <Button as={Link} to="/client-requests" variant="secondary" iconRight={<FiArrowRight size={14} />}>
              Examiner
            </Button>
          </div>
        </section>
      )}

      <section className="mt-5">
        <Card padding="none">
          {migrationsRolledUp && (
            <Link
              to="/migrations"
              className="group flex items-start gap-2 border-b border-line px-4 py-3 hover:bg-surface-muted"
            >
              <Badge variant="danger" size="sm" className="mt-0.5 shrink-0">
                {FAMILY_LABELS.technique}
              </Badge>
              <span className="min-w-0 text-body-sm text-fg group-hover:underline">
                Mises à jour de base en attente sur {migrationsTenants.length} clients : à lancer depuis la page
                Migrations
              </span>
            </Link>
          )}
          {tenants.length > 0 && <ColumnHeaders />}
          {withSubjects.length > 0 && <ul className="divide-y divide-line">{withSubjects.map(renderRow)}</ul>}
          {quiet.length > 0 && (
            <>
              <div
                className={cn(
                  'flex items-center justify-between gap-3 px-4 py-3',
                  withSubjects.length > 0 && 'border-t border-line',
                )}
              >
                <p className="text-body-sm text-fg-muted">
                  {quiet.length}{' '}
                  {migrationsRolledUp
                    ? plural(quiet.length, 'client sans autre sujet', 'clients sans autre sujet')
                    : plural(quiet.length, 'client sans sujet ouvert', 'clients sans sujet ouvert')}
                </p>
                <Button variant="ghost" size="sm" onClick={() => setShowQuiet((v) => !v)}>
                  {showQuiet ? 'Masquer' : 'Afficher'}
                </Button>
              </div>
              {showQuiet && <ul className="divide-y divide-line border-t border-line">{quiet.map(renderRow)}</ul>}
            </>
          )}
          {tenants.length === 0 && (
            <EmptyState title="Aucun client" description="Aucun client n'est enregistré sur la plateforme." />
          )}
        </Card>
      </section>
    </>
  );
}

export default Dashboard;
